import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useCurrency } from "@/hooks/useUserCurrency";
import { getInstallmentAmount, round2, sumInstallments } from "@/lib/installments";
import { Lock } from "lucide-react";

interface EditInstallmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  loanId: string;
  amount: number;
  installments: number;
  paidInstallments: number;
  installmentValues?: unknown;
  /** Vencimento da próxima parcela em aberto */
  nextDueDate: Date;
  installmentFrequency?: "weekly" | "biweekly" | "monthly";
  onSaved?: () => void;
}

export function EditInstallmentsDialog({
  open,
  onOpenChange,
  loanId,
  amount,
  installments,
  paidInstallments,
  installmentValues,
  nextDueDate,
  installmentFrequency = "monthly",
  onSaved,
}: EditInstallmentsDialogProps) {
  const { format: fmtMoney } = useCurrency();
  const [values, setValues] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const count = Math.max(0, Number(installments) || 0);
  const paid = Math.max(0, Math.min(Number(paidInstallments) || 0, count));

  useEffect(() => {
    if (!open) return;
    setValues(
      Array.from({ length: count }, (_, i) =>
        String(getInstallmentAmount({ amount, installments: count, installmentValues }, i)),
      ),
    );
  }, [open, amount, count, installmentValues]);

  const dueDates = useMemo(() => {
    const step = (date: Date, direction: 1 | -1) => {
      const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (installmentFrequency === "weekly") next.setDate(next.getDate() + 7 * direction);
      else if (installmentFrequency === "biweekly") next.setDate(next.getDate() + 15 * direction);
      else next.setMonth(next.getMonth() + direction);
      return next;
    };
    const list: Date[] = new Array(count);
    let cursor = new Date(nextDueDate);
    for (let i = paid; i < count; i++) {
      list[i] = cursor;
      cursor = step(cursor, 1);
    }
    cursor = new Date(nextDueDate);
    for (let i = paid - 1; i >= 0; i--) {
      cursor = step(cursor, -1);
      list[i] = cursor;
    }
    return list;
  }, [count, paid, nextDueDate, installmentFrequency]);

  const newTotal = sumInstallments(values);
  const remaining = sumInstallments(values.slice(paid));

  const validate = (): number[] | null => {
    if (values.length !== count) {
      toast.error("Informe o valor de todas as parcelas");
      return null;
    }
    const parsed: number[] = [];
    for (const raw of values) {
      if (raw === undefined || String(raw).trim() === "") {
        toast.error("Nenhuma parcela pode ficar vazia");
        return null;
      }
      const n = parseFloat(String(raw));
      if (!Number.isFinite(n) || n <= 0) {
        toast.error("Os valores das parcelas devem ser maiores que zero");
        return null;
      }
      parsed.push(round2(n));
    }
    return parsed;
  };

  const handleSave = async () => {
    const parsed = validate();
    if (!parsed) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("loans")
        .update({
          installment_values: parsed,
          amount: sumInstallments(parsed),
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", loanId);

      if (error) throw error;

      toast.success("Valores das parcelas atualizados!");
      onOpenChange(false);
      onSaved?.();
    } catch (e: any) {
      console.error("[EditInstallmentsDialog] erro ao salvar", e);
      toast.error("Não foi possível salvar. Verifique sua conexão e tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Editar parcelas</DialogTitle>
            <DialogDescription>
              Parcelas já pagas ficam bloqueadas. Apenas as parcelas em aberto podem ser alteradas.
            </DialogDescription>
            <p className="text-xs text-muted-foreground notranslate" translate="no">
              {count} {count === 1 ? "parcela" : "parcelas"} · {paid} {paid === 1 ? "paga" : "pagas"}
            </p>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-1">
            <div className="space-y-2">

              {values.map((v, i) => {
                const isPaid = i < paid;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2 rounded-lg border p-2 ${
                      isPaid ? "border-border/40 bg-muted/40 opacity-70" : "border-border/60"
                    }`}
                  >
                    <div className="w-24 shrink-0">
                      <div className="text-xs font-semibold notranslate" translate="no">
                        Parcela {i + 1}
                      </div>
                      <div className="text-[11px] text-muted-foreground notranslate" translate="no">
                        {dueDates[i] ? format(dueDates[i], "dd/MM/yyyy", { locale: ptBR }) : "—"}
                      </div>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      value={v}
                      disabled={isPaid}
                      onChange={(e) => {
                        const next = [...values];
                        next[i] = e.target.value;
                        setValues(next);
                      }}
                    />
                    {isPaid && (
                      <Badge variant="secondary" className="gap-1 shrink-0">
                        <Lock className="h-3 w-3" /> Paga
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-1 rounded-xl bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Novo valor total</span>
              <span className="font-bold notranslate" translate="no">{fmtMoney(newTotal)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Novo saldo restante</span>
              <span className="font-bold text-primary notranslate" translate="no">{fmtMoney(remaining)}</span>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (validate()) setConfirmOpen(true);
              }}
              disabled={saving}
            >
              {saving ? "Salvando..." : "Salvar alterações"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar alteração das parcelas?</AlertDialogTitle>
            <AlertDialogDescription>
              O novo valor total do empréstimo será {fmtMoney(newTotal)} e o saldo restante{" "}
              {fmtMoney(remaining)}. Os pagamentos já registrados não são alterados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSave} disabled={saving}>
              Salvar alterações
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
